// Hybrid Classifier - On-device first, cloud fallback
// Uses Chrome Writer API for content classification

import { ChromeAIDetector } from './chrome-ai-detector';
import { API_BASE_URL } from '../config/settings';

export interface ClassificationResult {
  type?: 'Notes' | 'Resource' | 'Content';
  tags?: string[];
  resourceCategory?: string;
  project?: string;
  area?: string;
  summary?: string;
  contentFormat?: string;
  platform?: string;
  method: 'on-device' | 'cloud';
  duration: number;
  cost: number;
}

export class HybridClassifier {
  private static MAX_ON_DEVICE_LENGTH = 3000; // chars

  /**
   * Classify content
   */
  static async classify(content: string, title?: string): Promise<ClassificationResult> {
    const startTime = Date.now();

    // Try on-device first
    if (await this.canUseOnDevice(content.length)) {
      try {
        const classification = await this.classifyOnDevice(content, title);

        this.trackUsage('on-device', Date.now() - startTime, 0);

        return {
          ...classification,
          method: 'on-device',
          duration: Date.now() - startTime,
          cost: 0
        };
      } catch (error) {
        console.warn('⚠️ On-device classification failed, falling back to cloud:', error);
      }
    }

    // Fallback to cloud
    const classification = await this.classifyCloud(content, title);

    this.trackUsage('cloud', Date.now() - startTime, 0.0001);

    return {
      ...classification,
      method: 'cloud',
      duration: Date.now() - startTime,
      cost: 0.0001
    };
  }

  /**
   * Check if we can use on-device
   */
  private static async canUseOnDevice(contentLength: number): Promise<boolean> {
    if (contentLength > this.MAX_ON_DEVICE_LENGTH) {
      return false;
    }

    const availability = await ChromeAIDetector.getCached();
    return availability.writer?.canUse === true;
  }

  /**
   * Classify using Chrome Writer API (on-device)
   */
  private static async classifyOnDevice(content: string, title?: string): Promise<Partial<ClassificationResult>> {
    // @ts-ignore - Chrome AI API
    const writer = await self.ai.writer.create({
      sharedContext: `You are a content classifier. Analyze content and extract:
- type: Notes, Resource, or Content
- tags: relevant keywords (comma-separated)
- resourceCategory: content-creation, business, marketing, paid-ads, desarrollo-personal, programming, video-editing, recording-camera, ai-prompts, hyperfocus-dopamine, adsconcierge, site-projects, personal-brand, uncategorized
- project: project name if applicable
- summary: 1-2 sentence summary
- contentFormat: Reel, Video, Carousel, Image, Story, Thread, Blog Post, Article, Newsletter, Podcast
- platform: youtube, instagram, tiktok, linkedin, twitter, facebook, medium, notion, etc.

Return as JSON.`
    });

    // Build prompt
    let prompt = '';
    if (title) {
      prompt += `Title: ${title}\n\n`;
    }

    // Truncate content if needed
    const truncated = content.length > this.MAX_ON_DEVICE_LENGTH
      ? content.slice(0, this.MAX_ON_DEVICE_LENGTH)
      : content;

    prompt += `Content:\n${truncated}\n\nClassification (JSON):`;

    const response = await writer.write(prompt);

    // Cleanup
    writer.destroy();

    // Parse response
    return this.parseClassification(response);
  }

  /**
   * Classify using cloud AI
   */
  private static async classifyCloud(content: string, title?: string): Promise<Partial<ClassificationResult>> {
    const response = await fetch(`${API_BASE_URL}/ai/classify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content,
        title
      })
    });

    if (!response.ok) {
      throw new Error('Cloud classification failed');
    }

    const data = await response.json();
    return data.classification || data.data || {};
  }

  /**
   * Parse classification from AI response
   */
  private static parseClassification(response: string): Partial<ClassificationResult> {
    try {
      // Try to extract JSON from response
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return {
          type: this.normalizeType(parsed.type),
          tags: Array.isArray(parsed.tags) ? parsed.tags : this.parseTags(parsed.tags),
          resourceCategory: this.normalizeCategory(parsed.resourceCategory || parsed.category),
          project: parsed.project,
          area: parsed.area,
          summary: parsed.summary,
          contentFormat: parsed.contentFormat || parsed.format,
          platform: parsed.platform
        };
      }
    } catch (error) {
      console.warn('Failed to parse classification JSON:', error);
    }

    // Fallback: extract what we can
    return {
      type: 'Notes',
      tags: [],
      summary: response.slice(0, 200)
    };
  }

  /**
   * Normalize type to valid values
   */
  private static normalizeType(type?: string): 'Notes' | 'Resource' | 'Content' | undefined {
    if (!type) return undefined;

    const lower = type.toLowerCase();
    if (lower.includes('note')) return 'Notes';
    if (lower.includes('resource')) return 'Resource';
    if (lower.includes('content')) return 'Content';

    return 'Notes'; // Default
  }

  /**
   * Normalize category to valid values
   */
  private static normalizeCategory(category?: string): string | undefined {
    if (!category) return undefined;

    const validCategories = [
      'content-creation', 'business', 'marketing', 'paid-ads',
      'desarrollo-personal', 'programming', 'video-editing',
      'recording-camera', 'ai-prompts', 'hyperfocus-dopamine',
      'adsconcierge', 'site-projects', 'personal-brand', 'uncategorized'
    ];

    const lower = category.toLowerCase().replace(/\s+/g, '-');

    // Find closest match
    for (const valid of validCategories) {
      if (lower.includes(valid) || valid.includes(lower)) {
        return valid;
      }
    }

    return 'uncategorized';
  }

  /**
   * Parse tags from string
   */
  private static parseTags(tagsString?: string): string[] {
    if (!tagsString) return [];

    return tagsString
      .split(/[,;]/)
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0 && tag.length < 50)
      .slice(0, 10);
  }

  /**
   * Track usage statistics
   */
  private static trackUsage(method: 'on-device' | 'cloud', duration: number, cost: number): void {
    const stats = this.getStats();

    if (method === 'on-device') {
      stats.onDevice.count++;
      stats.onDevice.totalDuration += duration;
    } else {
      stats.cloud.count++;
      stats.cloud.totalDuration += duration;
      stats.cloud.totalCost += cost;
    }

    localStorage.setItem('ai_classifier_stats', JSON.stringify(stats));
  }

  /**
   * Get usage statistics
   */
  static getStats() {
    const cached = localStorage.getItem('ai_classifier_stats');
    return cached ? JSON.parse(cached) : {
      onDevice: { count: 0, totalDuration: 0 },
      cloud: { count: 0, totalDuration: 0, totalCost: 0 }
    };
  }

  /**
   * Clear statistics
   */
  static clearStats(): void {
    localStorage.removeItem('ai_classifier_stats');
  }
}
